import React, { useEffect } from 'react';

// Regular function that handles the printing logic
export const executePrint = (formulation, steps, phases) => {
  // Create print-specific stylesheet
  const printCSS = `
    @page { 
      size: auto; 
      margin: 20mm 15mm 20mm 15mm; 
    }
    @media print {
      body { 
        padding: 0 !important; 
        -webkit-print-color-adjust: exact;
      }
      .no-print { 
        display: none !important; 
      }
      .page-break {
        page-break-before: always;
      }
      .step-container {
        page-break-before: always;
        page-break-inside: avoid;
      }
      .step-container:first-child {
        page-break-before: auto;
      }
      .step-container.first-step {
        page-break-before: auto;
      }
    }
    body {
      font-family: Arial, sans-serif;
      line-height: 1.4;
      color: #000;
      padding: 0;
      max-width: none;
      margin: 0;
    }
    .header {
      text-align: center;
      font-size: 18pt;
      font-weight: bold;
      margin-bottom: 20px;
      padding-bottom: 10px;
    }
    .sub-header {
      font-size: 16pt;
      font-weight: bold;
      margin: 20px 0 10px 0;
      color: #1976d2;
      padding-bottom: 5px;
      page-break-after: avoid;
    }
    .procedure-steps-container {
      page-break-inside: avoid;
    }
    .step-header {
      font-size: 14pt;
      font-weight: bold;
      margin: 0 0 8px 0;
      background-color: #f5f5f5;
      padding: 8px 12px;
      page-break-after: avoid;
    }
    .section-title {
      font-size: 12pt;
      font-weight: bold;
      margin: 12px 0 6px 0;
      color: #333;
      background-color: #f8f9fa;
      padding: 6px 10px;
      border-radius: 4px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 8px 0;
      font-size: 10pt;
    }
    table, th, td {
      border: 1px solid #333;
    }
    th, td {
      padding: 6px 8px;
      text-align: left;
      vertical-align: top;
    }
    th {
      background-color: #f0f0f0;
      font-weight: bold;
      font-size: 9pt;
    }
    .info-table {
      margin: 8px 0;
    }
    .info-table td {
      border: 1px solid #ddd;
      padding: 4px 6px;
    }
    .info-table .label {
      font-weight: bold;
      background-color: #f8f9fa;
      width: 25%;
    }
    .tags-section {
      margin: 10px 0;
      padding: 8px;
      background-color: #f9f9f9;
      border-radius: 4px;
    }
    .tag-content {
      margin: 6px 0;
      padding: 4px;
      background-color: #fff;
    }
    .criticality-critical {
      background-color: #ffebee !important;
    }
    .criticality-high {
      background-color: #fff3e0 !important;
    }
    .phase-info {
      display: inline-block;
      background-color: #e3f2fd;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 9pt;
      font-weight: bold;
    }
    .timestamp {
      font-size: 8pt;
      color: #666;
      margin-top: 20px;
      text-align: right;
      padding-top: 10px;
    }
    ul, ol {
      margin: 4px 0;
      padding-left: 20px;
    }
    li {
      margin: 2px 0;
      font-size: 10pt;
    }
  `;

  // Get phases map
  const phasesMap = {};
  if (phases) {
    phases.forEach(phase => {
      phasesMap[phase.id] = phase;
    });
  }

  // Helper function to render field if it has value
  const renderField = (label, value) => {
    return `
      <tr>
        <td class="label">${label}</td>
        <td>${value || 'No'}</td>
      </tr>
    `;
  };

  // Helper function to render tag content
  const renderTagContent = (step, tagType) => {
    const tagData = step[tagType];
    
    let content = '';
    
    switch(tagType) {
      case 'ph_details':
        content = `
          <div class="tag-content">
            <h4>pH Measurement Protocol</h4>
            <table class="info-table">
              ${renderField('Target pH', tagData?.target_ph)}
              ${renderField('Maximum pH', tagData?.max_acceptable_ph)}
              ${renderField('Minimum pH', tagData?.min_acceptable_ph)}
              ${renderField('pH Meter Brand', tagData?.ph_meter_brand)}
              ${renderField('Equipment ID', tagData?.equipment_id)}
              ${renderField('Electrode Type', tagData?.electrode_style)}
              ${renderField('Calibration Schedule', tagData?.calibration_schedule)}
              ${renderField('pH 4 Buffer Brand', tagData?.buffer_4_brand)}
              ${renderField('pH 7 Buffer Brand', tagData?.buffer_7_brand)}
              ${renderField('pH 10 Buffer Brand', tagData?.buffer_10_brand)}
              ${renderField('Buffer Shelf Life', tagData?.buffer_shelf_life)}
              ${renderField('Buffer Storage Temp', tagData?.buffer_storage_temp)}
              ${renderField('Measurement Temperature', tagData?.measurement_temperature)}
              ${renderField('Stabilization Time', tagData?.stabilization_time)}
              ${renderField('Number of Readings', tagData?.number_of_readings)}
              ${renderField('Sample Volume', tagData?.sample_volume)}
              ${renderField('Acid for Adjustment', tagData?.acid_for_adjustment)}
              ${renderField('Base for Adjustment', tagData?.base_for_adjustment)}
              ${renderField('Adjustment Increment', tagData?.adjustment_increment)}
              ${renderField('Adjustment Wait Time', tagData?.adjustment_wait_time)}
              ${renderField('Out of Spec Procedure', tagData?.out_of_spec_procedure)}
              ${renderField('Notification Contacts', tagData?.notification_contacts)}
              ${renderField('Auto Temp Compensation', tagData?.auto_temp_compensation ? 'Yes' : 'No')}
              ${renderField('Trend Monitoring', tagData?.trend_monitoring ? 'Yes' : 'No')}
              ${renderField('Electrode Maintenance', tagData?.electrode_maintenance ? 'Yes' : 'No')}
            </table>
          </div>
        `;
        break;

      case 'qa_test_details':
        content = `
          <div class="tag-content">
            <h4>QA Test Point Specifications</h4>
            <table class="info-table">
              ${renderField('Test Type', tagData?.test_type)}
              ${renderField('Test Method', tagData?.test_method)}
              ${renderField('Target Result', tagData?.target_result)}
              ${renderField('Acceptance Range', tagData?.acceptance_range)}
              ${renderField('Number of Samples', tagData?.number_of_samples)}
              ${renderField('Allowed Failures', tagData?.allowed_failures)}
              ${renderField('Confidence Level', tagData?.confidence_level)}
              ${renderField('Sampling Method', tagData?.sampling_method)}
              ${renderField('Equipment Name', tagData?.equipment_name)}
              ${renderField('Calibration Schedule', tagData?.calibration_schedule)}
              ${renderField('Environment Requirements', tagData?.environment_requirements)}
              ${renderField('Qualified Personnel', tagData?.qualified_personnel)}
              ${renderField('Detection Threshold', tagData?.detection_threshold)}
              ${renderField('Quantification Threshold', tagData?.quantification_threshold)}
              ${renderField('Upper Warning Limit', tagData?.upper_warning_limit)}
              ${renderField('Lower Warning Limit', tagData?.lower_warning_limit)}
              ${renderField('Failure Response', tagData?.failure_response)}
              ${renderField('Failure Notification', tagData?.failure_notification)}
              ${renderField('Critical Test', tagData?.critical_test ? 'Yes' : 'No')}
              ${renderField('Release Test', tagData?.release_test ? 'Yes' : 'No')}
              ${renderField('Trend Tracking', tagData?.trend_tracking ? 'Yes' : 'No')}
              ${renderField('Statistical Control', tagData?.statistical_control ? 'Yes' : 'No')}
            </table>
          </div>
        `;
        break;

      case 'sample_details':
        content = `
          <div class="tag-content">
            <h4>Sample Pull Requirements & Protocol</h4>
            <table class="info-table">
              ${renderField('For Immediate Testing', tagData?.analytical_portion)}
              ${renderField('For Backup/Retest', tagData?.reserve_portion)}
              ${renderField('For Stability Studies', tagData?.stability_portion)}
              ${renderField('Sampling Strategy', tagData?.sampling_strategy)}
              ${renderField('Sampling Tools', tagData?.sampling_tools)}
              ${renderField('Sampling Locations', tagData?.sampling_locations)}
              ${renderField('Sample Combination', tagData?.sample_combination)}
              ${renderField('Container Material', tagData?.container_material)}
              ${renderField('Sterility Requirement', tagData?.sterility_requirement)}
              ${renderField('Storage Temperature', tagData?.storage_temperature)}
              ${renderField('Special Storage', tagData?.special_storage)}
              ${renderField('Sampler Name', tagData?.sampler_name)}
              ${renderField('Analyst Name', tagData?.analyst_name)}
              ${renderField('Tracking Method', tagData?.tracking_method)}
              ${renderField('Transfer Method', tagData?.transfer_method)}
              ${renderField('Label Information', tagData?.label_information)}
              ${renderField('Retention Time', tagData?.retention_time)}
              ${renderField('Testing Timeline', tagData?.testing_timeline)}
              ${renderField('Handling Instructions', tagData?.handling_instructions)}
              ${renderField('Homogeneity Check', tagData?.homogeneity_check ? 'Yes' : 'No')}
              ${renderField('Visual Inspection', tagData?.visual_inspection ? 'Yes' : 'No')}
              ${renderField('Temperature Check', tagData?.temperature_check ? 'Yes' : 'No')}
              ${renderField('Duplicate Samples', tagData?.duplicate_samples ? 'Yes' : 'No')}
            </table>
          </div>
        `;
        break;

      case 'stability_details':
        content = `
          <div class="tag-content">
            <h4>Stability Study Details</h4>
            <table class="info-table">
              ${renderField('Study Purpose', tagData?.study_purpose)}
              ${renderField('Study ID', tagData?.study_id)}
              ${renderField('Study Coordinator', tagData?.study_coordinator)}
              ${renderField('Study Duration', tagData?.study_duration)}
              ${renderField('Primary Condition', tagData?.primary_condition)}
              ${renderField('Humidity Control', tagData?.humidity_control)}
              ${renderField('Light Exposure', tagData?.light_exposure)}
              ${renderField('Stress Conditions', tagData?.stress_conditions)}
              ${renderField('Testing Frequency', tagData?.testing_frequency)}
              ${renderField('Custom Timepoints', tagData?.custom_timepoints)}
              ${renderField('Samples Per Timepoint', tagData?.samples_per_timepoint)}
              ${renderField('Package Configuration', tagData?.package_configuration)}
              ${renderField('Package Details', tagData?.package_details)}
              ${renderField('Package Orientation', tagData?.package_orientation)}
              ${renderField('Trend Analysis Method', tagData?.trend_analysis_method)}
              ${renderField('Regulatory Standard', tagData?.regulatory_standard)}
              ${renderField('Failure Response Plan', tagData?.failure_response_plan)}
              ${renderField('Report Distribution', tagData?.report_distribution)}
              ${renderField('Appearance Check', tagData?.appearance_check ? 'Yes' : 'No')}
              ${renderField('Odor Check', tagData?.odor_check ? 'Yes' : 'No')}
              ${renderField('pH Monitoring', tagData?.ph_monitoring ? 'Yes' : 'No')}
              ${renderField('Viscosity Testing', tagData?.viscosity_testing ? 'Yes' : 'No')}
              ${renderField('Microbial Testing', tagData?.microbial_testing ? 'Yes' : 'No')}
              ${renderField('Active Ingredient Assay', tagData?.active_ingredient_assay ? 'Yes' : 'No')}
              ${renderField('Preservative Effectiveness', tagData?.preservative_effectiveness ? 'Yes' : 'No')}
              ${renderField('Package Integrity', tagData?.package_integrity ? 'Yes' : 'No')}
              ${renderField('Headspace Analysis', tagData?.headspace_analysis ? 'Yes' : 'No')}
              ${renderField('Real Time Study', tagData?.real_time_study ? 'Yes' : 'No')}
              ${renderField('Accelerated Study', tagData?.accelerated_study ? 'Yes' : 'No')}
              ${renderField('Pull Extra Samples', tagData?.pull_extra_samples ? 'Yes' : 'No')}
            </table>
          </div>
        `;
        break;

      default:
        content = `
          <div class="tag-content">
            <h4>Unknown Tag Type</h4>
            <table class="info-table">
              ${renderField('Tag Type', tagType)}
              ${renderField('Data Available', 'No')}
            </table>
          </div>
        `;
        break;
    }

    return content;
  };

  // Generate step content
  const generateStepContent = (step, index) => {
    const stepClass = step.criticality_level === 'Critical' ? 'criticality-critical' : 
                     step.criticality_level === 'High' ? 'criticality-high' : '';
    
    return `
      <div class="step-container${index === 0 ? ' first-step' : ''}">
        <div class="step-header ${stepClass}">
          Step ${step.order || index + 1}: ${step.step_title || `Step ${step.order || index + 1}`}
          ${step.phase ? `<span class="phase-info">${phasesMap[step.phase]?.name || 'Phase'}</span>` : ''}
          ${step.criticality_level && step.criticality_level !== 'Standard' ? 
            `<span class="phase-info" style="background-color: ${
              step.criticality_level === 'Critical' ? '#ffcdd2' : 
              step.criticality_level === 'High' ? '#ffe0b2' : '#e1f5fe'
            };">${step.criticality_level}</span>` : ''}
        </div>

        ${step.instruction ? `
          <div class="section-title">Instructions</div>
          <p>${step.instruction}</p>
        ` : ''}

        <table class="info-table">
          ${renderField('Expected Duration', step.expected_duration)}
          ${renderField('Phase', phasesMap[step.phase]?.name || 'Unassigned')}
        </table>

        ${step.temperature_range || step.humidity_requirement || step.air_quality_requirement || 
          step.lighting_requirement || step.cleanliness_level || step.ventilation_requirement ? `
          <div class="section-title">Environmental Conditions</div>
          <table class="info-table">
            ${renderField('Temperature Range', step.temperature_range ? 
              `${step.temperature_range} ${step.temperature_unit === 'Celsius' ? '°C' : 
              step.temperature_unit === 'Fahrenheit' ? '°F' : 'K'}` : '')}
            ${renderField('Humidity', step.humidity_requirement)}
            ${renderField('Air Quality', step.air_quality_requirement)}
            ${renderField('Lighting', step.lighting_requirement)}
            ${renderField('Cleanliness', step.cleanliness_level)}
            ${renderField('Ventilation', step.ventilation_requirement)}
          </table>
        ` : ''}

        ${step.primary_equipment || step.secondary_equipment || step.tools_required || step.calibration_requirements ? `
          <div class="section-title">Equipment Requirements</div>
          <table class="info-table">
            ${renderField('Primary Equipment', step.primary_equipment)}
            ${renderField('Secondary Equipment', step.secondary_equipment)}
            ${renderField('Tools Required', step.tools_required)}
            ${renderField('Calibration', step.calibration_requirements)}
          </table>
        ` : ''}

        ${step.minimum_personnel_count || step.required_certifications || step.experience_level_required || step.supervision_requirements ? `
          <div class="section-title">Personnel Requirements</div>
          <table class="info-table">
            ${renderField('Minimum Personnel', step.minimum_personnel_count)}
            ${renderField('Certifications', step.required_certifications)}
            ${renderField('Experience Level', step.experience_level_required)}
            ${renderField('Supervision', step.supervision_requirements)}
          </table>
        ` : ''}

        ${step.ppe_requirements || step.hazard_identification || step.emergency_procedures || step.first_aid_requirements ? `
          <div class="section-title">Safety Requirements</div>
          <table class="info-table">
            ${renderField('PPE Requirements', step.ppe_requirements)}
            ${renderField('Hazards', step.hazard_identification)}
            ${renderField('Emergency Procedures', step.emergency_procedures)}
            ${renderField('First Aid', step.first_aid_requirements)}
          </table>
        ` : ''}

        ${step.expected_results || step.critical_parameters || step.acceptance_criteria || step.verification_method ? `
          <div class="section-title">Process Control</div>
          <table class="info-table">
            ${renderField('Expected Results', step.expected_results)}
            ${renderField('Critical Parameters', step.critical_parameters)}
            ${renderField('Acceptance Criteria', step.acceptance_criteria)}
            ${renderField('Verification Method', step.verification_method)}
          </table>
        ` : ''}

        ${step.documentation_level || step.record_keeping_requirements || step.approval_requirements ? `
          <div class="section-title">Documentation</div>
          <table class="info-table">
            ${renderField('Documentation Level', step.documentation_level)}
            ${renderField('Record Keeping', step.record_keeping_requirements)}
            ${renderField('Approvals', step.approval_requirements)}
          </table>
        ` : ''}

        ${step.troubleshooting_notes || step.common_deviations || step.corrective_actions || step.escalation_procedures ? `
          <div class="section-title">Troubleshooting</div>
          <table class="info-table">
            ${renderField('Troubleshooting Notes', step.troubleshooting_notes)}
            ${renderField('Common Deviations', step.common_deviations)}
            ${renderField('Corrective Actions', step.corrective_actions)}
            ${renderField('Escalation Procedures', step.escalation_procedures)}
          </table>
        ` : ''}

        <div class="section-title">Quality Control Tags</div>
        <div class="tags-section">
          ${renderTagContent(step, 'ph_details')}
          ${renderTagContent(step, 'qa_test_details')}
          ${renderTagContent(step, 'sample_details')}
          ${renderTagContent(step, 'stability_details')}
        </div>
      </div>
    `;
  };

  // Generate complete HTML
  const printHTML = `
    <div class="header">Manufacturing Procedure</div>
    
    <table class="info-table">
      <tr>
        <td class="label">Formulation</td>
        <td>${formulation?.name || 'Untitled Formulation'}</td>
      </tr>
      <tr>
        <td class="label">Formulation Code</td>
        <td>${formulation?.code || 'Not specified'}</td>
      </tr>
      <tr>
        <td class="label">Total Steps</td>
        <td>${steps.length}</td>
      </tr>
      <tr>
        <td class="label">Critical Steps</td>
        <td>${steps.filter(s => s.criticality_level === 'Critical').length}</td>
      </tr>
      <tr>
        <td class="label">High Priority Steps</td>
        <td>${steps.filter(s => s.criticality_level === 'High').length}</td>
      </tr>
      <tr>
        <td class="label">Generated</td>
        <td>${new Date().toLocaleString()}</td>
      </tr>
    </table>

    ${steps.length === 0 ? 
      `<div class="sub-header">Procedure Steps</div>
      <p>No procedure steps defined.</p>` : 
      `<div class="procedure-steps-container">
        <div class="sub-header">Procedure Steps</div>
        ${generateStepContent(steps[0], 0)}
      </div>
      ${steps.slice(1).map((step, index) => generateStepContent(step, index + 1)).join('')}`}

    <div class="timestamp">
      Generated on ${new Date().toLocaleString()}<br>
      Formulation: ${formulation?.name || 'Untitled'} | Code: ${formulation?.code || 'N/A'}
    </div>
  `;

  // Create hidden iframe and print
  let printFrame = document.getElementById('procedure-print-frame');
  if (printFrame) {
    document.body.removeChild(printFrame);
  }

  printFrame = document.createElement('iframe');
  printFrame.id = 'procedure-print-frame';
  printFrame.style.position = 'fixed';
  printFrame.style.top = '-9999px';
  printFrame.style.left = '-9999px';
  printFrame.style.width = '0';
  printFrame.style.height = '0';
  printFrame.style.border = '0';
  document.body.appendChild(printFrame);

  const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
  frameDoc.open();
  frameDoc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Manufacturing Procedure - ${formulation?.name || 'Untitled'}</title>
      <style>${printCSS}</style>
    </head>
    <body>
      ${printHTML}
    </body>
    </html>
  `);
  frameDoc.close();

  setTimeout(() => {
    try {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      
      setTimeout(() => {
        if (document.body.contains(printFrame)) {
          document.body.removeChild(printFrame);
        }
      }, 1000);
    } catch (error) {
      console.error('Print error:', error);
      if (document.body.contains(printFrame)) {
        document.body.removeChild(printFrame);
      }
    }
  }, 500);
};

// React component (for legacy compatibility if needed)
const PrintProcedure = ({ formulation, steps, phases }) => {
  useEffect(() => {
    executePrint(formulation, steps, phases);
  }, [formulation, steps, phases]);

  return null; // This component doesn't render anything visible
};

export default PrintProcedure;
